import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '..';
import { updateStatistics } from './statistics.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;

// the proxy list currently being fetched, so a second identical request reuses it
let inflight = null, inflightKey = null;
// disconnected list: requests in flight by key, the server being shown, and ordering so a slow reply cannot overwrite a newer one
const disInflight = new Map();
let disServer = null, disSeq = 0, disApplied = 0;

export const useAdminProxyStore = defineStore('admin-proxy', {
    state: () => ({
        xProxies: [],
        smartProxies: [],
        ppuiProxies: [],   // ProProxyUI ports
        serverNames: [],
        loading: false
    }),
    actions: {
        async loadProxies(serverName, options = {}) {
            const waitConns = options.waitConns === true;
            // the same list asked for twice at once (the search bar fires on name and type) is loaded once
            const key = `${serverName}|${waitConns}`;
            if (inflight && inflightKey === key) return inflight;
            inflightKey = key;
            inflight = this._loadProxies(serverName, waitConns).finally(() => { inflight = null; inflightKey = null; });
            return inflight;
        },
        async _loadProxies(serverName, waitConns) {
            const alertStore = useAlertStore();
            try {
                this.loading = true;
                // waitConns: the MAX CONNS filter needs complete live connection counts before it can filter
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load`, { serverName, waitConns });
                this.xProxies = res.totalProxies.xProxyResult
                this.smartProxies = res.totalProxies.proxySmartResult
                this.ppuiProxies = res.totalProxies.proProxyUiResult || []
                updateStatistics()
                this.loading = false;
                if (!waitConns) this.loadConns(serverName);   // counts arrive a moment later, the table is already on screen
            } catch (error) {
                this.loading = false;
                alertStore.error(error);
            }
        },
        // live ProxySmart connection counts, merged into the rows already shown
        async loadConns(serverName) {
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/conns`, { serverName });
                const byUuid = res?.conns || {};
                if (!Object.keys(byUuid).length) return;
                this.smartProxies = this.smartProxies.map((p) => (byUuid[p.uuid] ? { ...p, ...byUuid[p.uuid] } : p));
            } catch (_) { /* counts are extra information; the page works without them */ }
        },
        // Disconnected list. options.live === false asks for the quick list (database only); otherwise the server
        // confirms every port against its box first, which can take several seconds when a box is slow.
        async loadDisProxies(serverName, options = {}) {
            const alertStore = useAlertStore();
            const silent = options?.silent === true;
            const live = options?.live !== false;
            if (disServer === null) disServer = serverName;
            const key = `${serverName}|${live}`;
            if (disInflight.has(key)) return disInflight.get(key);
            const seq = ++disSeq;
            const run = (async () => {
                try {
                    if (!silent) this.loading = true;
                    const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/disload`, { serverName, live });
                    // a newer request has started since (another server chosen, or the live list already in): drop this one
                    if (seq < disApplied || serverName !== disServer) return;
                    disApplied = seq;
                    this.xProxies = res.totalProxies.xProxyResult
                    this.smartProxies = res.totalProxies.proxySmartResult
                    if (!live) for (const row of [...this.xProxies, ...this.smartProxies]) if (row.live_status === 'unknown') row.live_status = 'pending'
                    updateStatistics()
                } catch (error) {
                    alertStore.error(error);
                } finally {
                    if (!silent) this.loading = false;
                    disInflight.delete(key);
                }
            })();
            disInflight.set(key, run);
            return run;
        },
        // quick list first, then the live-checked one replaces it without the loading spinner
        async openDisProxies(serverName) {
            disServer = serverName;
            await this.loadDisProxies(serverName, { live: false });
            if (disServer === serverName) this.loadDisProxies(serverName, { live: true, silent: true });
        },
        async loadServerNames() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/admin/proxy/load-server-names`,);
                this.serverNames = res.serverNames
            } catch (error) {
                alertStore.error(error);
            }
        },
        // XPROXY_NEW: next free HTTP+SOCKS port from the server's range start (+ a free position)
        async nextPort(serverName) {
            try { return await fetchWrapper.post(`${baseUrl}/admin/proxy/next-port`, { serverName }); }
            catch (error) { useAlertStore().error(error); return null; }
        },
        // XPROXY_NEW: positions with their live status ({ value, title, status })
        async loadPositions(serverName) {
            try { const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load-position-list`, { serverName }); return res.positions || []; }
            catch (error) { useAlertStore().error(error); return []; }
        },
        async loadPositionList(serverName) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load-position-list`, { serverName });
                return res.positionList
            } catch (error) {
                alertStore.error(error);
            }
        },
        async load_PS_AvailableModemList(serverName) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/load-proxysmart-modem-list`, { serverName });
                return {modemList: res.modemList, httpList: res.httpList, socketList: res.socketList, suggest: res.suggest || null}   // suggest = next free HTTP/SOCKS pair from the server's range starts
            } catch (error) {
                alertStore.error(error);
            }
        },
        async createNewProxy(serverName, proxyRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/proxy/create`, { serverName, proxyRequest });
                updateStatistics()
                alertStore.success("New successfully added")
                return true;

            } catch (error) {
                alertStore.error(error);
                return false;
            }
        },
        async updateProxy(uuid, modem_type, modem_id, proxyRequest) {
            const alertStore = useAlertStore();
            try {
                await fetchWrapper.post(`${baseUrl}/admin/proxy/update`, { uuid, modem_type, modem_id, proxyRequest });
                if(modem_type === 'X_PROXY') {
                    this.xProxies = this.xProxies.map(xproxy => {
                        if(xproxy.uuid === uuid) {
                            xproxy = {
                                ...xproxy,
                                ...proxyRequest,
                                uploadLimitType: proxyRequest.uploadLimitType ? 'limited' : 'unlimited',
                                downloadLimitType: proxyRequest.downloadLimitType ? 'limited' : 'unlimited',
                                upDownLimitType: proxyRequest.upDownLimitType ? 'limited' : 'unlimited',
                            }
                        }
                        return xproxy;
                    })
                } else {
                    this.smartProxies = this.smartProxies.map(proxysmart => {
                        if(proxysmart.uuid === uuid) {
                            proxysmart = {
                                ...proxysmart,
                                ...proxyRequest,
                                bandLimitOutType: proxyRequest.bandLimitOutType ? 'limited' : 'unlimited',
                                bandLimitInType: proxyRequest.bandLimitInType ? 'limited' : 'unlimited',
                                quotaType: proxyRequest.quotaType ? 'limited' : 'unlimited',
                            }
                        }
                        return proxysmart;
                    })
                }
                updateStatistics()
                alertStore.success("Proxy updated")

            } catch (error) {
                alertStore.error(error);
            }
        },
        async updateAction(uuid) {
            const alertStore = useAlertStore();
            try{
                const res = await fetchWrapper.post(`${baseUrl}/admin/proxy/update-active`, {uuid});
                if(res.item.modem_type === "X_PROXY") {
                    const temp = this.xProxies
                    this.xProxies = temp.map(xproxy => {
                        if(res.item.uuid === xproxy.uuid) {
                            xproxy.active = res.item.active
                        }
                        return xproxy
                    })
                } else {
                    const temp = this.smartProxies
                    this.smartProxies = this.smartProxies.map(proxysmart => {
                        if(res.item.uuid === proxysmart.uuid) proxysmart.active =  res.item.active
                        return proxysmart
                    })
                }
                if(res.item.active === 'inactive') {
                    alertStore.success("Proxy paused")
                } else {
                    alertStore.success("Proxy resumed")
                }
            } catch(error) {
                alertStore.error(error)
            }

        },
        async deleteProxy(uuids) {
            const alertStore = useAlertStore();
            try {
                const promises = uuids.map(uuid => 
                    fetchWrapper.post(`${baseUrl}/admin/proxy/delete`, { uuids: [uuid] })
                );
                const results = await Promise.allSettled(promises);
                const successes = results.filter(r => r.status === 'fulfilled').length;
                const failures = results.filter(r => r.status === 'rejected');
                
                updateStatistics()
                
                if (failures.length === 0) {
                    alertStore.success(`Deleted ${successes} proxies`);
                } else {
                    console.warn('Some deletes failed:', failures);
                    alertStore.error(`${failures.length} deletes failed, ${successes} succeeded`);
                }
            } catch (error) {
                alertStore.error(error);
            }
        },
    },
});
